import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ThemePreference } from 'pi-kiosk-shared/theme';
import { useCookieConsent } from '../features/consent/CookieConsentProvider.js';
import { openCookieSettings } from '../features/consent/CookieConsentFooter.js';
import { useOptionalOverlayBackDismiss } from '../shared/context/OverlayBackDismissContext.js';
import {
  peekCustomerPwaInstallPrompt,
  subscribeCustomerPwaInstallPrompt,
  takeCustomerPwaInstallPrompt,
} from './pwa/customerPwaInstallPromptStore.js';
import { forceCustomerAppUpdateFromSettings } from './pwa/forceCustomerAppUpdateFromSettings.js';
import {
  hasPositiveCustomerPwaInstallEvidence,
  isCustomerPwaInstalled,
  markCustomerPwaInstallEvidence,
  stampCustomerPwaInstallEvidenceIfStandalone,
} from './pwa/installOfferCooldown.js';
import { isPaymentPending } from './pwa/paymentPendingGate.js';
import {
  CUSTOMER_CONTROL_RADIUS_MD_CLASS,
  CUSTOMER_MIN_TOUCH_HEIGHT_CLASS,
} from '../shared/ui/customerTouchTarget.js';
import { CustomerChromeTopPanel } from './CustomerChromeTopPanel.js';
import { CustomerLanguageToggle } from './CustomerLanguageToggle.js';
import { useTheme } from './ThemeProvider.js';

/**
 * BR-08: chrome panel width owned by CustomerChromeTopPanel /
 * CUSTOMER_CHROME_PANEL_WIDTH_CLASS (max-w-md) — not bound here. DEBT →
 * docs/governance/evidence/vcl-step6-wave3/BR08_SETTINGS.md
 */

export interface CustomerSettingsSheetProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

const THEME_OPTIONS: readonly {
  readonly value: Extract<ThemePreference, 'light' | 'dark'>;
  readonly labelKey: string;
  readonly fallbackKey: string;
}[] = [
  { value: 'light', labelKey: 'account.settings.themeLight', fallbackKey: 'theme.light' },
  { value: 'dark', labelKey: 'account.settings.themeDark', fallbackKey: 'theme.dark' },
];

const GATE_POLL_MS = 400;

function readNavigatorOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/i.test(ua) && !/CriOS/i.test(ua);
}

/**
 * Shell Settings panel — language / appearance / install & update always;
 * cookie settings for guests only. Signed-in cookie management lives under
 * Profile → Account & privacy.
 */
export function CustomerSettingsSheet({
  open,
  onClose,
}: CustomerSettingsSheetProps): JSX.Element {
  const { t } = useTranslation('customer');
  const overlayBack = useOptionalOverlayBackDismiss();
  const registerOverlayDismiss = overlayBack?.registerOverlayDismiss;
  const consent = useCookieConsent();
  const { preference, effectiveTheme, setTheme } = useTheme();
  const selectedTheme: 'light' | 'dark' =
    preference === 'light' || preference === 'dark' ? preference : effectiveTheme;
  const showCookieSettings = !consent.isLoggedIn;
  const [isUpdating, setIsUpdating] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [paymentPending, setPaymentPending] = useState(() => isPaymentPending());
  const [navigatorOffline, setNavigatorOffline] = useState(() => readNavigatorOffline());
  const [offlineAttemptHint, setOfflineAttemptHint] = useState(false);
  const [runningAsInstalledApp, setRunningAsInstalledApp] = useState(() => {
    stampCustomerPwaInstallEvidenceIfStandalone();
    return isCustomerPwaInstalled();
  });
  const [hasInstallPrompt, setHasInstallPrompt] = useState(
    () => peekCustomerPwaInstallPrompt() !== null,
  );
  const [installHint, setInstallHint] = useState<
    'already' | 'ios' | 'unavailable' | null
  >(null);

  useEffect(() => {
    if (!open || registerOverlayDismiss === undefined) {
      return;
    }
    return registerOverlayDismiss(onClose);
  }, [open, onClose, registerOverlayDismiss]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const syncGates = (): void => {
      setPaymentPending(isPaymentPending());
      stampCustomerPwaInstallEvidenceIfStandalone();
      setRunningAsInstalledApp(isCustomerPwaInstalled());
      const offline = readNavigatorOffline();
      setNavigatorOffline(offline);
      if (!offline) {
        setOfflineAttemptHint(false);
      }
    };

    // queueMicrotask: avoid react-hooks/set-state-in-effect (cascading render)
    queueMicrotask(syncGates);
    const intervalId = window.setInterval(syncGates, GATE_POLL_MS);
    window.addEventListener('online', syncGates);
    window.addEventListener('offline', syncGates);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('online', syncGates);
      window.removeEventListener('offline', syncGates);
    };
  }, [open]);

  useEffect(() => {
    const syncPrompt = (): void => {
      setHasInstallPrompt(peekCustomerPwaInstallPrompt() !== null);
    };
    syncPrompt();
    return subscribeCustomerPwaInstallPrompt(syncPrompt);
  }, []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onAppInstalled = (): void => {
      markCustomerPwaInstallEvidence();
      setRunningAsInstalledApp(true);
      setInstallHint(null);
      takeCustomerPwaInstallPrompt();
      setHasInstallPrompt(false);
    };
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, [open]);

  const languageLabel = t('chrome.settings.language', {
    defaultValue: t('more.utilities.language'),
  });
  const appearanceLabel = t('chrome.settings.appearance', {
    defaultValue: t('account.settings.appearanceTitle'),
  });
  const installUpdateLabel = t('chrome.settings.installAndUpdate', {
    defaultValue: t('chrome.settings.update'),
  });
  const cookiesSectionLabel = t('account.settings.cookiesTitle', {
    defaultValue: t('consent.regionLabel'),
  });
  const cookiesActionLabel = t('chrome.settings.cookies', {
    defaultValue: t('consent.settingsLink'),
  });

  const updateDisabled = isUpdating || paymentPending;
  const showPaymentHint = paymentPending;
  const showOfflineHint =
    !paymentPending && (navigatorOffline || offlineAttemptHint);
  const showInstallButton = !runningAsInstalledApp;

  const handleCookieSettings = useCallback(() => {
    openCookieSettings(consent.openBanner);
    onClose();
  }, [consent.openBanner, onClose]);

  const handleUpdate = useCallback(async (): Promise<void> => {
    if (isUpdating || isPaymentPending()) {
      return;
    }
    setOfflineAttemptHint(false);
    setIsUpdating(true);
    try {
      const outcome = await forceCustomerAppUpdateFromSettings();
      if (outcome === 'blocked_payment') {
        setIsUpdating(false);
        return;
      }
      if (outcome === 'offline') {
        setOfflineAttemptHint(true);
        setIsUpdating(false);
        return;
      }
    } catch {
      setIsUpdating(false);
    }
  }, [isUpdating]);

  const handleInstall = useCallback(async (): Promise<void> => {
    if (isInstalling || isUpdating || isPaymentPending()) {
      return;
    }
    setInstallHint(null);

    // Positive evidence first (standalone, persisted flag, getInstalledRelatedApps).
    // Also covers iOS Safari browser-tab after a prior Home Screen / standalone open.
    // Prefer hasPositive over a sync standalone shortcut so stamp+deferred BIP → already+Update.
    if (await hasPositiveCustomerPwaInstallEvidence()) {
      markCustomerPwaInstallEvidence();
      setInstallHint('already');
      await handleUpdate();
      return;
    }

    // take() — exclusive ownership so Lifecycle banner cannot prompt() a spent BIP.
    const promptEvent = takeCustomerPwaInstallPrompt();
    if (promptEvent !== null) {
      setHasInstallPrompt(false);
      setIsInstalling(true);
      try {
        await promptEvent.prompt();
        const choice = await promptEvent.userChoice;
        if (choice.outcome === 'accepted') {
          markCustomerPwaInstallEvidence();
        }
      } catch {
        // Browser may reject prompt(); leave Install available if a new BIP arrives.
      } finally {
        setIsInstalling(false);
      }
      return;
    }

    if (isIosSafari()) {
      // Pure Safari tab, no install evidence → Share / A2HS guide only.
      setInstallHint('ios');
      return;
    }

    // No BIP and not positively installed — do not overclaim "already installed".
    setInstallHint('unavailable');
  }, [handleUpdate, isInstalling, isUpdating]);

  return (
    <CustomerChromeTopPanel
      open={open}
      onClose={onClose}
      title={t('chrome.settings.title')}
      titleId="customer-settings-sheet-title"
      closeLabel={t('shared.cancel')}
      testId="customer-settings-sheet"
      className="bg-[var(--color-surface)]"
    >
      <div
        className="flex w-full flex-col gap-[var(--vcl-space-stack-md)] px-2 py-2"
        data-testid="customer-settings-preferences"
      >
        <section
          className="flex w-full flex-col gap-[var(--vcl-space-stack-sm)]"
          aria-labelledby="customer-settings-language-label"
        >
          <h3
            id="customer-settings-language-label"
            className="text-[length:var(--vcl-type-label-size)] font-[number:var(--vcl-type-label-weight)] text-[var(--color-on-surface)]"
          >
            {languageLabel}
          </h3>
          <CustomerLanguageToggle
            placement="header"
            dataTestId="customer-settings-language-toggle"
          />
        </section>

        <section
          className="flex w-full flex-col gap-[var(--vcl-space-stack-sm)]"
          aria-labelledby="customer-settings-appearance-label"
        >
          <h3
            id="customer-settings-appearance-label"
            className="text-[length:var(--vcl-type-label-size)] font-[number:var(--vcl-type-label-weight)] text-[var(--color-on-surface)]"
          >
            {appearanceLabel}
          </h3>
          <div
            className="flex flex-wrap gap-[var(--vcl-gap-inline-sm)]"
            role="radiogroup"
            aria-labelledby="customer-settings-appearance-label"
            data-testid="customer-settings-appearance"
          >
            {THEME_OPTIONS.map((option) => {
              const selected = selectedTheme === option.value;
              const optionLabel = t(option.labelKey, {
                defaultValue: t(option.fallbackKey),
              });
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  data-testid={`customer-settings-theme-${option.value}`}
                  className={[
                    'customer-btn-press inline-flex min-w-[10rem] items-center justify-center border px-7 transition-colors',
                    CUSTOMER_MIN_TOUCH_HEIGHT_CLASS,
                    CUSTOMER_CONTROL_RADIUS_MD_CLASS,
                    'text-[length:var(--vcl-type-label-size)] font-[number:var(--vcl-type-label-weight)]',
                    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]',
                    selected
                      ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-[var(--color-accent-foreground)]'
                      : 'border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-on-surface)] hover:bg-[var(--color-surface-elevated)]',
                  ].join(' ')}
                  onClick={() => {
                    setTheme(option.value);
                  }}
                >
                  {optionLabel}
                </button>
              );
            })}
          </div>
        </section>

        <section
          className="flex w-full flex-col gap-[var(--vcl-space-stack-sm)]"
          aria-labelledby="customer-settings-install-update-label"
        >
          <h3
            id="customer-settings-install-update-label"
            className="text-[length:var(--vcl-type-label-size)] font-[number:var(--vcl-type-label-weight)] text-[var(--color-on-surface)]"
          >
            {installUpdateLabel}
          </h3>
          <div className="flex w-full flex-wrap gap-[var(--vcl-gap-inline-sm)]">
            {showInstallButton ? (
              <button
                type="button"
                disabled={isInstalling || isUpdating || paymentPending}
                aria-busy={isInstalling || undefined}
                data-testid="customer-settings-install"
                data-has-prompt={hasInstallPrompt ? 'true' : 'false'}
                className={[
                  'customer-btn-press inline-flex min-w-[10rem] max-w-[50%] flex-1 items-center justify-center border border-[var(--color-border)]',
                  CUSTOMER_MIN_TOUCH_HEIGHT_CLASS,
                  CUSTOMER_CONTROL_RADIUS_MD_CLASS,
                  'bg-[var(--color-surface)] px-5 text-[length:var(--vcl-type-label-size)] font-[number:var(--vcl-type-label-weight)] text-[var(--color-on-surface)]',
                  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]',
                  isInstalling || isUpdating || paymentPending
                    ? 'cursor-not-allowed opacity-60'
                    : 'hover:bg-[var(--color-surface-elevated)]',
                ].join(' ')}
                onClick={() => {
                  void handleInstall();
                }}
              >
                {isInstalling
                  ? t('chrome.settings.installBusy')
                  : t('chrome.settings.installAction', {
                      defaultValue: t('pwa.installAction'),
                    })}
              </button>
            ) : null}
            <button
              type="button"
              disabled={updateDisabled}
              aria-busy={isUpdating || undefined}
              data-testid="customer-settings-update"
              className={[
                'customer-btn-press inline-flex min-w-[10rem] max-w-[50%] flex-1 items-center justify-center border border-[var(--color-border)]',
                CUSTOMER_MIN_TOUCH_HEIGHT_CLASS,
                CUSTOMER_CONTROL_RADIUS_MD_CLASS,
                'bg-[var(--color-surface)] px-5 text-[length:var(--vcl-type-label-size)] font-[number:var(--vcl-type-label-weight)] text-[var(--color-on-surface)]',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]',
                updateDisabled
                  ? 'cursor-not-allowed opacity-60'
                  : 'hover:bg-[var(--color-surface-elevated)]',
              ].join(' ')}
              onClick={() => {
                void handleUpdate();
              }}
            >
              {isUpdating
                ? t('chrome.settings.updateBusy')
                : t('chrome.settings.updateAction')}
            </button>
          </div>
          {installHint === 'already' ? (
            <p
              className="text-xs text-[var(--color-on-surface-muted,var(--color-on-surface))] opacity-80"
              data-testid="customer-settings-install-hint"
            >
              {t('chrome.settings.installAlreadyInstalled')}
            </p>
          ) : null}
          {installHint === 'ios' ? (
            <p
              className="text-xs text-[var(--color-on-surface-muted,var(--color-on-surface))] opacity-80"
              data-testid="customer-settings-install-hint"
            >
              {t('chrome.settings.installIosGuide', {
                defaultValue: t('pwa.iosInstallGuide'),
              })}
            </p>
          ) : null}
          {installHint === 'unavailable' ? (
            <p
              className="text-xs text-[var(--color-on-surface-muted,var(--color-on-surface))] opacity-80"
              data-testid="customer-settings-install-hint"
            >
              {t('chrome.settings.installUnavailable')}
            </p>
          ) : null}
          {showPaymentHint ? (
            <p
              className="text-xs text-[var(--color-on-surface-muted,var(--color-on-surface))] opacity-80"
              data-testid="customer-settings-update-hint"
            >
              {t('chrome.settings.updateDeferredPayment')}
            </p>
          ) : null}
          {showOfflineHint ? (
            <p
              className="text-xs text-[var(--color-on-surface-muted,var(--color-on-surface))] opacity-80"
              data-testid="customer-settings-update-hint"
            >
              {t('chrome.settings.updateOffline')}
            </p>
          ) : null}
        </section>

        {showCookieSettings ? (
          <section
            className="flex w-full flex-col gap-[var(--vcl-space-stack-sm)]"
            aria-labelledby="customer-settings-cookies-label"
          >
            <h3
              id="customer-settings-cookies-label"
              className="text-[length:var(--vcl-type-label-size)] font-[number:var(--vcl-type-label-weight)] text-[var(--color-on-surface)]"
            >
              {cookiesSectionLabel}
            </h3>
            <button
              type="button"
              className={[
                'customer-btn-press inline-flex max-w-sm items-center justify-center border border-[var(--color-border)]',
                CUSTOMER_MIN_TOUCH_HEIGHT_CLASS,
                CUSTOMER_CONTROL_RADIUS_MD_CLASS,
                'bg-[var(--color-surface)] px-5 text-[length:var(--vcl-type-label-size)] font-[number:var(--vcl-type-label-weight)] text-[var(--color-on-surface)]',
                'hover:bg-[var(--color-surface-elevated)]',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]',
              ].join(' ')}
              data-testid="customer-settings-cookies"
              onClick={handleCookieSettings}
            >
              {cookiesActionLabel}
            </button>
          </section>
        ) : null}
      </div>
    </CustomerChromeTopPanel>
  );
}
